import _ from 'lodash';
import * as yup from 'yup';

import { invoiceCreateSchema } from '~/components/InvoiceTools/schema/invoice.schema';

import { PROMO_PAYMENT_TYPE, VOUCHER_PAYMENT_TYPE } from '~/consts/payment';

import { request } from '~/services/common';

const HOTEL_DESCRIPTION = 'Hotel Name: \nLocation: \nTraveller(s): \nTravel dates: \nBooking Number: ';
const FLIGHT_DESCRIPTION = 'Traveller(s): \nRoute(s): \nTravel dates: \nPNR(s): \nSeat(s): ';
const CAR_HIRE_DESCRIPTION = 'Driver(s): \nVehicle details: \nHire dates: \nBooking Number: ';

export type InvoiceFormData = yup.InferType<typeof invoiceCreateSchema>;

interface GenerateInvoiceResponse {
  s3Path: string;
}

interface GetSignedDownloadLinkResponse {
  downloadLink: string;
}

export async function generateInvoice(formData: InvoiceFormData): Promise<GenerateInvoiceResponse> {
  // Format dates as ISO strings for the API
  const payload = {
    ...formData,
    date: formData.date instanceof Date ? formData.date.toISOString() : formData.date,
    payments: formData.payments.map((payment) => ({
      ...payment,
      date: payment.date instanceof Date ? payment.date.toISOString() : payment.date,
    })),
  };

  return request(`${window.configs.API_HOST}/api/pdf/v2/invoice/v1/generate-invoice`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function getSignedInvoiceDownloadLink(s3Uri: string): Promise<string> {
  const response: GetSignedDownloadLinkResponse = await request(
    `${window.configs.API_HOST}/api/pdf/v2/document/v1/get-signed-document-download-link?s3Uri=${encodeURIComponent(
      s3Uri,
    )}`,
  );
  return response.downloadLink;
}

export function getLineItemsFromOrder(order: App.Order): Array<{
  description: string;
  bookingNumber: string;
  totalPrice: number;
  gstAmount: number;
  type: string;
  id: string;
}> {
  const totalPaymentFees = order.merchant_fees.reduce((acc, fee) => acc + Number(fee.amount), 0);

  return [
    order.accommodation_items?.flatMap((item) => [
      {
        description: HOTEL_DESCRIPTION,
        bookingNumber: item.booking_number || 'N/A',
        totalPrice: item.total || 0,
        gstAmount: item.gst_amount || 0,
        type: 'accommodation',
        id: item.id_items,
      },
      ...(Number(item.promo_code_discount_amount)
        ? [
            {
              description: 'Promo code discount',
              bookingNumber: '',
              totalPrice: Number(item.promo_code_discount_amount) * -1,
              gstAmount: 0,
              type: 'accommodation',
              id: item.id_items,
            },
          ]
        : []),
    ]),
    order.bedbank_items?.flatMap((item) => [
      {
        description: HOTEL_DESCRIPTION,
        bookingNumber: item.booking_number || 'N/A',
        totalPrice: item.total || 0,
        gstAmount: item.gst_amount || 0,
        type: 'bedbank',
        id: item.id,
      },
      ...(Number(item.promo_code_discount_amount)
        ? [
            {
              description: 'Promo code discount',
              bookingNumber: '',
              totalPrice: Number(item.promo_code_discount_amount) * -1,
              gstAmount: 0,
              type: 'bedbank',
              id: item.id,
            },
          ]
        : []),
    ]),
    order.business_credit_items?.map((item) => ({
      description: 'Credits with purchase',
      bookingNumber: 'N/A',
      totalPrice: item.total || 0,
      gstAmount: 0,
      type: 'business_credit',
      id: item.id,
    })),
    order.car_hire_items?.flatMap((item) => [
      {
        description: CAR_HIRE_DESCRIPTION,
        bookingNumber: item.reservation?.providerReservationCode || 'N/A',
        totalPrice: item.total || 0,
        gstAmount: item.gst_amount || 0,
        type: 'car_hire',
        id: item.id_car_hire_items,
      },
      ...(Number(item.promo_code_discount_amount)
        ? [
            {
              description: 'Promo code discount',
              bookingNumber: '',
              totalPrice: Number(item.promo_code_discount_amount) * -1,
              gstAmount: 0,
              type: 'car_hire',
              id: item.id_car_hire_items,
            },
          ]
        : []),
    ]),
    order.custom_offer_items?.map((item) => ({
      description: ' ',
      bookingNumber: 'N/A',
      totalPrice: item.total || 0,
      gstAmount: item.gst_amount || 0,
      type: 'custom_offer',
      id: item.id,
    })),
    order.flight_items?.flatMap((item) => [
      {
        description: FLIGHT_DESCRIPTION,
        bookingNumber: item.pnr_id,
        totalPrice: item.total || 0,
        gstAmount: item.gst_amount || 0,
        type: 'flight',
        id: item.id,
      },
      ...(Number(item.promo_code_discount_amount)
        ? [
            {
              description: 'Promo code discount',
              bookingNumber: '',
              totalPrice: Number(item.promo_code_discount_amount) * -1,
              gstAmount: 0,
              type: 'flight',
              id: item.id,
            },
          ]
        : []),
    ]),
    order.offline_flight_items?.map((item) => ({
      description: FLIGHT_DESCRIPTION,
      bookingNumber: item.booking_number || 'N/A',
      totalPrice: item.total || 0,
      gstAmount: item.gst_amount || 0,
      type: 'offline_flight',
      id: item.id_offline_flight_items,
    })),
    order.service_fee_items?.map((item) => ({
      description: 'Service Fees',
      bookingNumber: 'N/A',
      totalPrice: item.total || 0,
      gstAmount: item.gst_amount || 0,
      type: 'service_fee',
      id: item.id,
    })),
    totalPaymentFees
      ? [
          {
            description: 'Payment Fees',
            bookingNumber: 'N/A',
            totalPrice: totalPaymentFees,
            gstAmount: 0,
            type: 'payment_fee',
            id: 'payment_fee',
          },
        ]
      : undefined,
  ]
    .flat()
    .filter(Boolean);
}

function formatPaymentDescription(payment: App.Order['payments'][0]): string {
  const paymentMetadata = payment.response_json?.data?.object?.charges?.data?.[0]?.payment_method_details;
  if (paymentMetadata?.type === 'card') {
    return `Card ending in xxxx-${paymentMetadata.card?.last4 ?? paymentMetadata.card?.last_4}`;
  }
  if (payment.type) {
    return _.startCase(_.toLower(payment.type));
  }
  return 'Payment';
}

export function getPaymentsForOrder(order: App.Order) {
  return (
    order.payments
      ?.filter((payment) => payment.success !== false)
      ?.filter((payment) => payment.type !== PROMO_PAYMENT_TYPE && payment.type !== VOUCHER_PAYMENT_TYPE)
      .map((payment) => ({
        description: formatPaymentDescription(payment),
        amount: payment.amount || 0,
        date: payment.created_at ? new Date(payment.created_at) : new Date(),
      })) || []
  );
}

export function extractDocumentIdFromS3Uri(s3Uri: string): string {
  // Extract filename from S3 URI, e.g., "invoice-LEBT-4C3EA9-1.pdf"
  const filename = s3Uri.split('/').pop() || '';
  // Remove "invoice-" prefix and ".pdf" extension to get "LEBT-4C3EA9-1"
  return filename.replace(/^invoice-/, '').replace(/\.pdf$/, '');
}
